import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CreditCard, Loader2, CloudOff, Receipt, Wallet, AlertTriangle, ChevronRight, FlaskConical } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { PLAN_CLS, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

const SUB_STATUS = {
  active: { label: "Aktiv", cls: "text-status-success" },
  trialing: { label: "Trial", cls: "text-status-warning" },
  cancelled: { label: "Gekündigt", cls: "text-fundio-muted" },
  canceled: { label: "Gekündigt", cls: "text-fundio-muted" },
  past_due: { label: "Überfällig", cls: "text-status-danger" },
};
const chf = (n) => (n == null ? "—" : `CHF ${Number(n).toLocaleString("de-CH")}`);

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="card-surface rounded-xl p-4">
      <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-fundio-muted"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="font-display text-2xl font-semibold text-fundio-ink">{value}</div>
    </div>
  );
}

export default function AbosRechnungen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/all-billing", { params: { scope } })
      .then((r) => setState(r.data))
      .catch(() => setState({ service_available: false, tenants: [] }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const rows = state.tenants || [];
  const provider = state.payment_provider;
  const totalOpen = rows.reduce((s, r) => s + (r.open_total || 0), 0);
  const totalInvoices = rows.reduce((s, r) => s + (r.invoice_count || 0), 0);
  const mrr = rows.reduce((s, r) => s + (["active", "trialing"].includes(r.status) ? (r.price_monthly_chf || 0) : 0), 0);

  return (
    <div data-testid="abos-rechnungen-page">
      <PageHeader title="Abos & Rechnungen" subtitle="Abonnements, Entitlements und Rechnungen der FUNDIO-Kunden – live aus der kanonischen Platform-Admin-API." />

      {!provider && (
        <div data-testid="billing-nopay-banner" className="mb-6 flex items-start gap-3 rounded-xl border border-status-warning-border bg-status-warning-bg/50 px-5 py-4">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-status-warning-bg text-status-warning"><AlertTriangle className="h-5 w-5" /></span>
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-wider text-status-warning">ONLINE-ZAHLUNGSPROVIDER NOCH NICHT VERBUNDEN</p>
            <p className="mt-1 text-sm text-fundio-muted">Es ist kein automatischer Zahlungsanbieter (Stripe/TWINT) aktiv. Rechnungen werden ausschliesslich durch eine ausdrückliche, <span className="font-semibold">auditierte manuelle Admin-Bestätigung</span> als bezahlt markiert. Es werden keine Fake-Zahlungen simuliert.</p>
          </div>
        </div>
      )}

      {!state.service_available ? (
        <div data-testid="abos-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
          <p className="mt-1 text-sm text-fundio-muted">{state.reason || "Keine Live-Daten, keine Ersatzdaten."}</p>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi icon={CreditCard} label="Aktive Abos" value={rows.length} testid="abos-kpi-count" />
            <Kpi icon={Wallet} label="Listenpreis / Monat" value={chf(mrr)} testid="abos-kpi-mrr" />
            <Kpi icon={Receipt} label="Rechnungen gesamt" value={totalInvoices} testid="abos-kpi-invoices" />
            <Kpi icon={AlertTriangle} label="Offen (Summe)" value={chf(totalOpen)} testid="abos-kpi-open" />
          </div>

          <Panel title={`Abonnements (${rows.length})`} testid="abos-plans-panel" noPad>
            {!rows.length ? (
              <div className="p-4"><EmptyState icon={CreditCard} title="Keine Abonnements" description="Im gewählten Bereich sind keine Abos vorhanden." testid="abos-empty" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                      <th className="px-5 py-3">Kunde</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Periode</th><th className="px-5 py-3">Preis / Mt</th><th className="px-5 py-3">Rechnungen</th>
                      <th className="px-5 py-3">Offen</th><th className="px-5 py-3 text-right">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-fundio-border">
                    {rows.map((r) => {
                      const st = SUB_STATUS[r.status] || { label: r.status || "—", cls: "text-fundio-muted" };
                      return (
                        <tr key={r.tenant_id} data-testid={`abos-row-${r.tenant_id}`} className="bg-white hover:bg-fundio-subtle">
                          <td className="px-5 py-3 font-medium text-fundio-ink">
                            <span className="flex items-center gap-2">{r.company_name || r.tenant_id}
                              {r.is_demo && <span className="inline-flex items-center gap-1 rounded border border-status-warning-border bg-status-warning-bg/60 px-1.5 py-0.5 text-[10px] font-semibold text-status-warning"><FlaskConical className="h-3 w-3" />QA</span>}
                            </span>
                          </td>
                          <td className="px-5 py-3"><span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${PLAN_CLS[r.plan] || PLAN_CLS.FREE}`}>{r.plan || "—"}</span></td>
                          <td className="px-5 py-3"><span className={`text-xs font-medium ${st.cls}`}>{st.label}</span>{r.status === "trialing" && r.trial_end && <span className="ml-1 text-[11px] text-fundio-faint">bis {formatDate(r.trial_end)}</span>}</td>
                          <td className="px-5 py-3 text-fundio-muted">{r.billing_period === "yearly" ? "Jährlich" : r.billing_period === "monthly" ? "Monatlich" : "—"}</td>
                          <td className="px-5 py-3 text-fundio-muted">{r.price_monthly_chf != null ? `${r.currency || "CHF"} ${r.price_monthly_chf}` : "—"}</td>
                          <td className="px-5 py-3 text-fundio-muted">{r.invoice_count || 0}</td>
                          <td className="px-5 py-3">{r.open_total ? <span className="font-medium text-status-warning">{r.currency || "CHF"} {r.open_total}</span> : <span className="text-fundio-faint">—</span>}</td>
                          <td className="px-5 py-3 text-right">
                            <button data-testid={`abos-open-${r.tenant_id}`} onClick={() => navigate(`/brockis/${r.tenant_id}`)} className="inline-flex items-center gap-1 rounded-lg border border-fundio-border px-2.5 py-1.5 text-xs font-medium text-fundio-ink hover:bg-white">Öffnen<ChevronRight className="h-3.5 w-3.5" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
